import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { appointmentApi } from '@services/appointment.service'
import { Button } from '@components/ui/button'
import { Label } from '@components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'

interface Props {
  doctorId: string
}

const SLOT_OPTIONS = [
  { value: '15', label: '15 min' },
  { value: '20', label: '20 min' },
  { value: '30', label: '30 min' },
  { value: '45', label: '45 min' },
  { value: '60', label: '60 min' },
]

const LEAD_TIME_OPTIONS = [
  { value: '30', label: '30 min' },
  { value: '60', label: '1 hora' },
  { value: '120', label: '2 horas' },
  { value: '240', label: '4 horas' },
  { value: '1440', label: '24 horas' },
]

const HORIZON_OPTIONS = [
  { value: '7', label: '7 dias' },
  { value: '14', label: '14 dias' },
  { value: '30', label: '30 dias' },
  { value: '60', label: '60 dias' },
  { value: '90', label: '90 dias' },
]

export function AvailabilitySettingsCard({ doctorId }: Props) {
  const queryClient = useQueryClient()
  const [slotMinutes, setSlotMinutes] = useState('30')
  const [leadTime, setLeadTime] = useState('120')
  const [maxHorizon, setMaxHorizon] = useState('60')
  const [isActive, setIsActive] = useState(true)

  const { data: rule } = useQuery({
    queryKey: ['availability-rule', doctorId],
    queryFn: () => appointmentApi.getAvailabilityRule(doctorId),
  })

  useEffect(() => {
    if (rule) {
      setSlotMinutes(String(rule.slot_minutes))
      setLeadTime(String(rule.lead_time_minutes))
      setMaxHorizon(String(rule.max_horizon_days))
      setIsActive(rule.is_active)
    }
  }, [rule])

  const mutation = useMutation({
    mutationFn: () =>
      appointmentApi.upsertAvailabilityRule(doctorId, {
        slot_minutes: Number(slotMinutes),
        lead_time_minutes: Number(leadTime),
        max_horizon_days: Number(maxHorizon),
        is_active: isActive,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability-rule', doctorId] })
    },
  })

  return (
    <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
      <div className="border-b bg-ml-surface-container-low px-6 py-3.5">
        <h2 className="text-base font-semibold">Configuración General</h2>
        <p className="text-xs text-muted-foreground">Duración de citas, anticipación y horizonte de reserva</p>
      </div>
      <div className="space-y-4 p-6">

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Duracion de cita</Label>
          <Select value={slotMinutes} onValueChange={setSlotMinutes}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {SLOT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Tiempo minimo de anticipacion</Label>
          <Select value={leadTime} onValueChange={setLeadTime}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEAD_TIME_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Horizonte maximo de reserva</Label>
          <Select value={maxHorizon} onValueChange={setMaxHorizon}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {HORIZON_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-end pt-2">
        <Button onClick={() => mutation.mutate()} disabled={mutation.isPending}>
          {mutation.isPending ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>
      </div>
    </div>
  )
}
